
import { useState, useRef, useCallback } from "react";
import { 
  generateContextualDelay, 
  FINAL_MESSAGE_DELAY, 
  FINAL_MESSAGES, 
  CONTEXTUAL_WAITING_MESSAGES,
  TIME_BASED_MESSAGES,
  COMPLEXITY_BASED_MESSAGES,
  getTimeBasedMessage
} from "@/constants/chatMessages";
import { analyzeMessageContext, getMessageComplexity, getCurrentTimeContext } from "@/utils/chatContextAnalyzer";
import type { WaitingMessage, MessageCategory } from "@/types/chat";

export function useWaitingMessages() {
  const [waitingMessages, setWaitingMessages] = useState<WaitingMessage[]>([]);
  const [isShowingWaitingMessages, setIsShowingWaitingMessages] = useState(false);
  const [waitingMessageCategory, setWaitingMessageCategory] = useState<MessageCategory>('GENERALE');
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const messageCountRef = useRef(0);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current = [];
  }, []);

  const selectContextualMessage = useCallback((category: MessageCategory, messageIndex: number, userMessage: string) => {
    const messages = CONTEXTUAL_WAITING_MESSAGES[category];
    const complexity = getMessageComplexity(userMessage);
    const timeContext = getCurrentTimeContext();
    
    // Prima priorità: messaggio basato su orario (20% chance)
    if (messageIndex === 0 && Math.random() < 0.2) {
      const timeBasedMessage = getTimeBasedMessage(category);
      if (timeBasedMessage) return timeBasedMessage;
    }
    
    // Seconda priorità: messaggi basati su complessità (30% chance)
    if (messageIndex <= 1 && Math.random() < 0.3) {
      const complexityMessages = COMPLEXITY_BASED_MESSAGES[complexity];
      if (complexityMessages) {
        return complexityMessages[Math.floor(Math.random() * complexityMessages.length)];
      }
    }
    
    // Messaggi standard specifici per categoria
    if (!messages || messages.length === 0) {
      return CONTEXTUAL_WAITING_MESSAGES['GENERALE'][messageIndex % CONTEXTUAL_WAITING_MESSAGES['GENERALE'].length];
    }
    
    // Evita ripetizioni negli ultimi 3 messaggi
    const recentMessages = waitingMessages.slice(-3).map(m => m.text);
    let attempts = 0;
    let selectedMessage;
    
    do {
      const randomIndex = Math.floor(Math.random() * messages.length);
      selectedMessage = messages[randomIndex];
      attempts++;
    } while (recentMessages.includes(selectedMessage) && attempts < 5);
    
    return selectedMessage;
  }, [waitingMessages]);

  const addWaitingMessage = useCallback((category: MessageCategory, userMessage: string) => {
    const messageIndex = messageCountRef.current;
    const text = selectContextualMessage(category, messageIndex, userMessage);
    
    const newMessage: WaitingMessage = {
      id: `waiting-${Date.now()}-${Math.random()}`,
      text,
      category,
      delay: generateContextualDelay(category, messageIndex),
      priority: messageIndex + 1
    };
    
    messageCountRef.current++;
    setWaitingMessages(prev => [...prev, newMessage]);
  }, [selectContextualMessage]);

  const startWaitingMessages = useCallback((userMessage: string) => {
    // Reset stato
    setWaitingMessages([]);
    setIsShowingWaitingMessages(true);
    messageCountRef.current = 0;
    clearTimers();
    
    // Analizza il contesto del messaggio
    const category = analyzeMessageContext(userMessage);
    const complexity = getMessageComplexity(userMessage);
    setWaitingMessageCategory(category);
    
    // Determina il numero di messaggi basato sulla complessità
    const messageCount = complexity === 'simple' ? 3 : complexity === 'medium' ? 4 : 5;
    
    let cumulativeDelay = 0;
    let isActive = true;
    
    // Sequenza di messaggi progressiva
    for (let i = 0; i < messageCount; i++) {
      const contextualDelay = generateContextualDelay(category, i);
      cumulativeDelay += contextualDelay;
      
      const timer = setTimeout(() => {
        if (isActive) {
          addWaitingMessage(category, userMessage);
        }
      }, cumulativeDelay);
      
      timersRef.current.push(timer);
    }
    
    // Messaggio finale con delay ridotto
    const finalTimer = setTimeout(() => {
      if (isActive) {
        const finalMessage: WaitingMessage = {
          id: `final-${Date.now()}`,
          text: FINAL_MESSAGES[Math.floor(Math.random() * FINAL_MESSAGES.length)],
          category,
          delay: FINAL_MESSAGE_DELAY,
          priority: 999
        };
        setWaitingMessages(prev => [...prev, finalMessage]);
      }
    }, cumulativeDelay + FINAL_MESSAGE_DELAY);
    
    timersRef.current.push(finalTimer);
    
    // Cleanup function
    return () => {
      isActive = false;
      clearTimers();
    };
  }, [addWaitingMessage, clearTimers]);

  const stopWaitingMessages = useCallback(() => {
    clearTimers();
    setIsShowingWaitingMessages(false);
    setWaitingMessages([]);
    messageCountRef.current = 0;
  }, [clearTimers]);

  return {
    waitingMessages,
    isShowingWaitingMessages,
    waitingMessageCategory,
    startWaitingMessages,
    stopWaitingMessages
  };
}
